// Inline address pill used on the landing page in place of the various
// "Get Your Free Assessment" CTAs. Mirrors LookupAddressForm's autosuggest
// behaviour exactly (same redux actions, same suggestion source) but drops
// the title and county-support note, and routes to /verify after a
// suggestion is picked. The /verify page sees `selectedAddress` already
// in redux and skips its own LookupAddressForm step, landing the user
// directly on the home-info verification step.
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Form,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Spinner,
} from "reactstrap";
import Autosuggest from "react-autosuggest";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";

import { formatAddress } from "../../utils";
import {
  AddressNotFound,
  AddressPillWrap,
  SmallSpinner,
  SuggestionsDropdown,
} from "../FreeAssessmentPage/styled";
import "../FreeAssessmentPage/autosuggest-theme.css";
import { SUPPORT_EMAIL } from "../../constants";
import {
  getAddressSuggestions as getAddressSuggestionsAction,
  getExternalAddressSuggestions as getExternalAddressSuggestionsAction,
  getAddressSuggestionsSuccess,
  setAddress,
} from "../../redux/actions/assessment";

const HomepageAddressEntry = ({
  addressSuggestions,
  isLoading,
  getAddressSuggestions,
  getExternalSuggestions,
  clearSuggestions,
  selectAddress,
  placeholder,
}) => {
  const history = useHistory();
  const [address, setLocalAddress] = useState("");
  const selectedSuggestionRef = useRef(null);
  const wrapperRef = useRef(null);
  const [suggestionStarted, setSuggestionStarted] = useState(true);
  // Bumped on scroll/resize while the dropdown is open so the portal
  // re-reads the input's viewport position and stays anchored to it.
  const [, repositionTick] = useState(0);
  // Tracks per-instance focus so we only swap home→spinner on the pill
  // the user is typing in. addressSuggestions / isLoading live in shared
  // redux state, so without this gate every instance would flicker
  // simultaneously.
  const [hasFocus, setHasFocus] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleCallSuggestions = useCallback(
    debounce((str) => {
      getAddressSuggestions(str);
      setSuggestionStarted(false);
    }, 400),
    [getAddressSuggestions]
  );

  useEffect(() => {
    return () => handleCallSuggestions.cancel();
  }, [handleCallSuggestions]);

  // Keep the portaled dropdown anchored to the input while it's open.
  useEffect(() => {
    if (!addressSuggestions.length) return undefined;
    const onMove = () => repositionTick((n) => n + 1);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [addressSuggestions.length]);

  const handleChange = (e, { newValue }) => {
    // Clear the redux selectedAddress only on the FIRST keystroke
    // after a suggestion was picked — once cleared, further typing
    // doesn't need to re-dispatch (it'd be a no-op for the store but
    // still triggers a useSelector pass + re-render in every
    // connected component).  Sets the ref to null after the dispatch
    // so subsequent keystrokes skip the work.  INP win at the most
    // common interaction on the most-trafficked page.
    if (
      selectedSuggestionRef.current !== null &&
      newValue !== selectedSuggestionRef.current
    ) {
      selectAddress({});
      selectedSuggestionRef.current = null;
    }
    setLocalAddress(newValue);
  };

  const onSuggestionFetchRequested = ({ value }) => {
    if (value === address) return;
    if (value.trim() !== "") {
      setSuggestionStarted(true);
      return handleCallSuggestions(value);
    }
    clearSuggestions();
  };

  const getSuggestionValue = (suggestion) => {
    const formatted = formatAddress(suggestion);
    selectedSuggestionRef.current = formatted;
    selectAddress(suggestion);
    // Covered DB addresses go to the report flow; addresses that only
    // came back from the external geocoder (``covered: false``) route to
    // the /coverage interest page, which logs the demand for expansion.
    history.push(suggestion.covered === false ? "/coverage" : "/verify");
    return formatted;
  };

  const renderSuggestion = (suggestion) => (
    <span>{formatAddress(suggestion)}</span>
  );

  // Show the "search every state" escape hatch only when the current
  // results are our DB matches (not already geocoder results).  Lets the
  // user reach Photon even when the DB returned loose token matches.
  const showEscapeHatch =
    addressSuggestions.length > 0 &&
    !addressSuggestions.some((s) => s.covered === false);

  // Fixed (viewport-anchored) position read from the input's rect, so the
  // portaled dropdown sits directly under the pill regardless of where this
  // instance lives in the page.
  const dropdownStyle = () => {
    const el =
      wrapperRef.current &&
      wrapperRef.current.querySelector(".react-autosuggest__input");
    if (!el) return { position: "fixed", visibility: "hidden" };
    const r = el.getBoundingClientRect();
    return { position: "fixed", top: r.bottom + 6, left: r.left, width: r.width, zIndex: 4000 };
  };

  const renderSuggestionsContainer = ({ containerProps, children }) => {
    const node = (
      <SuggestionsDropdown {...containerProps} style={dropdownStyle()}>
        {children}
        {showEscapeHatch && (
          <button
            type="button"
            className="address-escape-hatch"
            // preventDefault on mousedown keeps focus on the input so the
            // dropdown doesn't blur-close before the geocoder results swap in.
            onMouseDown={(e) => {
              e.preventDefault();
              const q = address.trim();
              if (q) getExternalSuggestions(q);
            }}
          >
            <i className="fa fa-search" aria-hidden="true" />
            <span>
              Don't see your address? <strong>Search every state →</strong>
            </span>
          </button>
        )}
      </SuggestionsDropdown>
    );
    // Mount on document.body so the dropdown escapes ancestor overflow /
    // transform / stacking contexts (the reported "paints under the next
    // section" bug). React events still bubble through the portal.
    return typeof document !== "undefined"
      ? ReactDOM.createPortal(node, document.body)
      : node;
  };

  const renderAddressNotFound = () => {
    if (
      address.trim() !== "" &&
      addressSuggestions.length < 1 &&
      !isLoading &&
      !suggestionStarted
    ) {
      return (
        <AddressNotFound>
          We couldn't find that address. Please enter the full street address,
          or contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> for help.
        </AddressNotFound>
      );
    }
  };

  // Prefetch the /verify chunk the moment the user signals intent
  // (focus or first keystroke).  By the time they pick a suggestion,
  // ``getSuggestionValue`` calls history.push("/verify") and
  // App.js's React.lazy resolves from the prefetch instead of a cold
  // network round-trip.  This was the dominant contributor to the
  // INP-on-mobile metric Search Console flagged in May 2026 — the
  // route change after a suggestion-pick averaged 200ms+ because
  // the FreeAssessmentPage chunk hadn't even started downloading
  // until the click fired.  Webpack returns the same module promise
  // on repeat calls, so triggering the import multiple times is
  // free once the chunk is cached.
  const prefetchVerify = useCallback(() => {
    import(
      /* webpackPrefetch: true */
      "../FreeAssessmentPage"
    ).catch(() => {
      // Network blip — the real navigation will retry from the
      // user's tap, no point surfacing this prefetch failure.
    });
  }, []);

  const inputProps = {
    onChange: handleChange,
    onFocus: () => {
      setHasFocus(true);
      prefetchVerify();
    },
    onBlur: () => setHasFocus(false),
    value: address,
    placeholder,
    type: "address",
    "aria-label": "Enter your property address",
  };

  const showSpinner = isLoading && hasFocus;

  // The wrapping div lifts this instance into its own stacking context
  // above neighbouring sections — without it, sibling sections (with their
  // own position/transform-induced stacking contexts) paint on top of the
  // dropdown. Dropping `alwaysRenderSuggestions` means only the focused
  // pill renders suggestions, so typing in one instance doesn't open the
  // dropdown on the other instances that read the same redux slice.
  return (
    <div ref={wrapperRef} style={{ position: "relative", zIndex: 1000, width: "100%" }}>
      <Form
        className="register-form"
        onSubmit={(e) => e.preventDefault()}
        style={{ width: "100%" }}
      >
        <AddressPillWrap>
          <InputGroup className="form-group-no-border">
            <InputGroupAddon addonType="prepend">
              <InputGroupText>
                {showSpinner ? (
                  <SmallSpinner as={Spinner} color="info" />
                ) : (
                  <i className="fa fa-home" />
                )}
              </InputGroupText>
            </InputGroupAddon>
            <Autosuggest
              suggestions={addressSuggestions}
              renderSuggestion={renderSuggestion}
              renderSuggestionsContainer={renderSuggestionsContainer}
              onSuggestionsFetchRequested={onSuggestionFetchRequested}
              getSuggestionValue={getSuggestionValue}
              inputProps={inputProps}
              renderInputComponent={(p) => <Input {...p} />}
            />
          </InputGroup>
        </AddressPillWrap>
        {renderAddressNotFound()}
      </Form>
    </div>
  );
};

HomepageAddressEntry.propTypes = {
  addressSuggestions: PropTypes.array,
  isLoading: PropTypes.bool,
  getAddressSuggestions: PropTypes.func.isRequired,
  getExternalSuggestions: PropTypes.func.isRequired,
  clearSuggestions: PropTypes.func.isRequired,
  selectAddress: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

HomepageAddressEntry.defaultProps = {
  addressSuggestions: [],
  isLoading: false,
  placeholder: "Enter your property address",
};

const mapStateToProps = (state) => ({
  addressSuggestions: state.freeAssessment.addressSuggestions,
  isLoading: state.freeAssessment.loading.addressSuggestions,
});

const mapDispatchToProps = (dispatch) => ({
  getAddressSuggestions: (str) => dispatch(getAddressSuggestionsAction(str)),
  getExternalSuggestions: (str) => dispatch(getExternalAddressSuggestionsAction(str)),
  clearSuggestions: () => dispatch(getAddressSuggestionsSuccess([])),
  selectAddress: (address) => dispatch(setAddress(address)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HomepageAddressEntry);
